import { Component, OnDestroy, OnInit } from '@angular/core';
import { LoggingService } from '../../shared/services/logging';
import { select, Store } from '@ngrx/store';
import * as fromUserSetting from '../../user-setting/store';
import {
    combineLatest,
    Observable,
    ReplaySubject,
    BehaviorSubject
} from 'rxjs';
import {
    filter,
    map,
    switchMap,
    takeWhile,
    takeUntil,
    tap
} from 'rxjs/operators';
import { Actions, ofType } from '@ngrx/effects';
import * as UserSettingActions from '../../user-setting/store/user-setting.actions';
import * as fromUserInfo from '../../user-info';
import * as fromAuth from '../../auth/store';
import * as fromRoot from '../../store';
import * as fromSettings from '../../company-setting';
import { AuthenticationService } from '../../auth/authentication.service';

@Component({
    selector: 'ef-password-change-feature',
    templateUrl: './password-change-feature.component.html',
    styleUrls: ['./password-change-feature.component.scss'],
    standalone: false
})
export class PasswordChangeFeatureComponent implements OnInit, OnDestroy {
    userInfo$: Observable<fromUserInfo.IUserInfo>;
    userInfo: fromUserInfo.IUserInfo;
    validateOldPasswordResponse$: Observable<boolean>;
    infoMessage$: Observable<string>;
    minPasswordLength$: Observable<number>;
    validateOldPasswordSubject = new ReplaySubject<string>(1);
    updatePasswordResponseStatusCode$ = new BehaviorSubject<string>('');
    updatePasswordResponseMessage$ = new BehaviorSubject<string>('');
    private active = true;

    constructor(
        private log: LoggingService,
        private authService: AuthenticationService,
        private store: Store<fromRoot.State>,
        private actions$: Actions
    ) {}

    ngOnInit(): void {
        this.store.dispatch(
            fromSettings.getCompanySetting({ SettingType: 'MinPasswordLength' })
        );       
        sessionStorage.removeItem('userSettingsResponseStatusCode');
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngOnDestroy(): void {
        this.active = false;
    }
    setupObservables() {
        this.userInfo$ = this.store.select(fromUserInfo.getUserInfo);
    }
    setupSubscriptions() {      
        this.userInfo$
            .pipe(takeWhile(() => this.active))
            .subscribe(userInfo => {
                if (userInfo) {
                    this.store.dispatch(
                        fromUserSetting.getUserSettings({
                            EmployeeListID: userInfo.EmployeeListID,
                            EmpSettingID: fromUserSetting.UserSettingType.PasswordWarning
                        })
                    );

                    this.userInfo = { ...userInfo };
                } else {
                    this.log.warn('No User Info Found');
                }
            });

        this.infoMessage$ = this.store.pipe(
            select(fromUserSetting.getSettingValue('PasswordWarning')),
            takeWhile(() => this.active),
            tap(settings =>
                this.log.verbose({
                    type: 'PasswordChangeFeatureComponent:setupSubscriptions:PasswordWarning',
                    settings
                })
            )
        );

        this.minPasswordLength$ = this.store.pipe(
            select(fromSettings.getSettingValue('MinPasswordLength')),
            takeWhile(() => this.active),
            map(setting => parseInt(setting)),
            tap(setting =>
                this.log.verbose({
                    type: 'PasswordChangeFeatureComponent:setupSubscriptions',
                    setting
                })
            )
        );

        this.validateOldPasswordResponse$ = combineLatest([
            this.userInfo$,
            this.validateOldPasswordSubject,
            this.store.pipe(select(fromAuth.getAuth))
        ]).pipe(
            takeWhile(() => this.active),
            switchMap(([userInfo, oldPassword, auth]) => {
                if (!userInfo || !auth) {
                    return [];
                }
                return this.authService
                    .authenticate({
                        username: userInfo.EUserID,
                        password: oldPassword,
                        pin: auth.pin
                    })
                    .pipe(map(response => !response.HasError));
            })
        );

        // Listen for upsertUserSettings action to get response status
        this.actions$
            .pipe(
                ofType(UserSettingActions.upsertUserSettings),
                takeWhile(() => this.active)
            )
            .subscribe(() => {
                // Get the response status from sessionStorage after the effect updates it
                setTimeout(() => {
                    const statusCode = sessionStorage.getItem(
                        'userSettingsResponseStatusCode'
                    );
                    
                    if (statusCode) {
                        const parsedStatusCode =
                            JSON.parse(statusCode).toString();
                        this.updatePasswordResponseStatusCode$.next(
                            parsedStatusCode
                        );

                        // If status code indicates failure, get error message from ErrorMessages
                        if (parsedStatusCode !== '1') {
                            const errorMessages = sessionStorage.getItem(
                                'userSettingsErrorMessages'
                            );
                            if (errorMessages) {
                                const parsedErrorMessages =
                                    JSON.parse(errorMessages);
                                if (
                                    parsedErrorMessages &&
                                    parsedErrorMessages.length > 0
                                ) {
                                    this.updatePasswordResponseMessage$.next(
                                        parsedErrorMessages[0]
                                    );
                                } else {
                                    this.updatePasswordResponseMessage$.next(
                                        'Password update failed'
                                    );
                                }
                            } else {
                                this.updatePasswordResponseMessage$.next(
                                    'Password update failed'
                                );
                            }
                        } else {
                            // Clear error message on success
                            this.updatePasswordResponseMessage$.next('');
                            //this needs to be the string version of the enum, i.e. 'TemporaryPassword' or 'PasswordWarning'
                            
                            this.store.dispatch(UserSettingActions.deleteUserSetting({ id: fromUserSetting.UserSettingType[fromUserSetting.UserSettingType.TemporaryPassword] }));
                            this.store.dispatch(UserSettingActions.deleteUserSetting({ id: fromUserSetting.UserSettingType[fromUserSetting.UserSettingType.PasswordWarning] }));
                        }                       
                    }
                }, 0);
            });
    }

    onValidateOldPassword(oldPassword: string) {
        // call login api to check if login is successful
        console.log('Validating old password:', oldPassword);
        this.validateOldPasswordSubject.next(oldPassword);
    }
    onUpdatePassword(password: string) {
        // call update password api to change the password
        const setting = {
            CtrlType: 'password',
            EmpSettingID: 100,
            EmpSettingType: 'password',
            EmpSettingValue: password,
            EmployeeListID: this.userInfo.EmployeeListID
        };
        this.store.dispatch(
            fromUserSetting.putUserSettings({ settings: [setting] })
        );
    }
    onLogout() {
        this.store.dispatch(new fromAuth.Logout());
    }
}
